import { ApolloError } from '@apollo/client'
import { AppGenericRegular, SpinnerIosRegular } from '@fluentui/react-icons'
import clsx from 'clsx'
import { useEffect, useState } from 'react'
import { HierarchicalFilterContextProvider } from '@/contexts/HierarchicalFilterContext/HierarchicalFilterContext'
import ErrorBoundary from '@/ui-legacy/ErrorBoundary/ErrorBoundary'
import { Centered } from '../Centered'
import { ContextPane } from '../ContextPane'
import { Page } from '../Page'
import { usePlatformOutletContext } from '../Platform/usePlatFormOutletContext'
import SupportPane from '../SupportPane/SupportPane'
import * as g from './App.generated'
import './App.scss'
import { PageFilterContextProvider2 } from '@/contexts/PageFilterContext/PageFilterContextProvider2'
import { PageProvider } from '@/contexts/PageProvider/PageProvider'
import { FilterIdsProvider } from '@/contexts/FilterIdProvider/FilterIdsProvider'

function useAppOrNull(): {
  app: g.AppInfoFragment | null
  loading: boolean
  error: ApolloError | undefined
} {
  const { appKey, projectId } = usePlatformOutletContext()

  const { data, loading, error } = g.useAppQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      appId: appKey,
      projectId,
      filters: [],
    },
  })
  return {
    app: data?.app ?? null,
    loading,
    error,
  }
}

export enum SupportPaneSize {
  Max = 'max',
  Min = 'min',
}

function UnityApp() {
  const { appId } = usePlatformOutletContext()
  const { app, loading, error } = useAppOrNull()
  const { pageId } = usePlatformOutletContext()
  const [appFound, setAppFound] = useState<boolean>(true)

  const [supportPaneKey, setSupportPaneKey] = useState<string>()
  const [supportPaneSize, setSupportPaneSize] = useState<SupportPaneSize>()

  useEffect(() => {
    if (!loading && !error && !app) {
      setAppFound(false)
    }
  }, [loading, error, app])

  useEffect(() => {
    setSupportPaneSize(undefined)
  }, [pageId])

  const setSupport = (key: string) => {
    setSupportPaneKey(key)
    setSupportPaneSize(SupportPaneSize.Min)
  }

  const content = (
    <div className={clsx('ux-app !h-[calc(100vh-86px)]', supportPaneSize)}>
      <ContextPane setSupport={setSupport} />
      {supportPaneSize && supportPaneKey && (
        <SupportPane
          supportPaneKey={supportPaneKey}
          supportPaneSize={supportPaneSize}
          setSupportPaneSize={setSupportPaneSize}
          appId={appId ?? null}
        />
      )}
      <FilterIdsProvider>
        <PageFilterContextProvider2>
          <HierarchicalFilterContextProvider>
            <div className="content !h-[calc(100vh-86px)]">
              <ErrorBoundary>
                {appFound ? (
                  <PageProvider>
                    <Page />
                  </PageProvider>
                ) : (
                  <Centered>App not found</Centered>
                )}
              </ErrorBoundary>
            </div>
          </HierarchicalFilterContextProvider>
        </PageFilterContextProvider2>
      </FilterIdsProvider>
    </div>
  )

  return <>{loading ? <UnityAppPageLoading /> : content}</>
}

export function UnityAppPageLoading() {
  return (
    <div className="flex size-full items-center justify-center bg-muted">
      <div className="flex flex-col items-center justify-center gap-y-8">
        <AppGenericRegular fontSize={72} className="text-foreground/50" />
        <div className="flex items-center gap-x-2">
          <SpinnerIosRegular fontSize={20} className="animate-spin" />
          <span>Please wait...</span>
        </div>
      </div>
    </div>
  )
}

export default UnityApp
